import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getNotificationDestination, markNotificationRead } from '../api/notifications';
import { Notification, NotificationDestination } from '../types/notification';

/** Router state key RouteNotice reads to show a one-off message. */
export const ROUTE_NOTICE_STATE_KEY = 'notice';

const NOTIFICATION_QUERY_KEYS = [
  'notifications-recent',
  'notifications-page',
  'notification-summary',
];

/** Notification click-through (Phase 94): marks the notification read (the
 * existing behaviour), asks the server where it should go — the exact
 * record when it exists and the user may open it — and navigates there.
 * When the record can't be opened, the server's notice is shown (via
 * RouteNotice) on the fallback list page, or on the current page when
 * there's nowhere the user may go. */
export const useOpenNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  return useCallback(
    async (notification: Notification) => {
      if (!notification.readAt) {
        markNotificationRead(notification.id)
          .then(() =>
            NOTIFICATION_QUERY_KEYS.forEach((key) =>
              queryClient.invalidateQueries({ queryKey: [key] }),
            ),
          )
          .catch(() => undefined);
      }

      let destination: NotificationDestination;
      try {
        destination = await getNotificationDestination(notification.id);
      } catch {
        destination = { path: null, notice: "Couldn't open this notification. Please try again." };
      }

      const state = destination.notice
        ? { [ROUTE_NOTICE_STATE_KEY]: destination.notice }
        : undefined;
      if (destination.path) {
        navigate(destination.path, { state });
      } else if (state) {
        navigate(`${location.pathname}${location.search}`, { replace: true, state });
      }
    },
    [navigate, location.pathname, location.search, queryClient],
  );
};
